/**
 * DignityConversation — Unified chat UI for the Dignity AI healthcare advisor.
 *
 * Combines:
 * - Conversational intake collection (quick replies + free text)
 * - Inline payment gate
 * - Post-payment AI chat with scenario preview sidebar
 * - Session controls (message counter + finalize)
 *
 * All in a single scrolling chat thread.
 */

import { useState, useRef, useEffect } from 'react';
import { Send, ChevronUp, ChevronDown, SkipForward } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { DignityAvatar } from './DignityAvatar';
import { InlineChatPaymentGate } from './InlineChatPaymentGate';
import { AiScenarioPreview } from './AiScenarioPreview';
import { AiSessionControls } from './AiSessionControls';
import { useDignityChat, type FinalizedScenario } from '@/hooks/useDignityChat';
import type { DignityMessage } from '@/hooks/useDignityChat';

interface DignityConversationProps {
  onFinalized: (scenario: FinalizedScenario) => void;
}

export function DignityConversation({ onFinalized }: DignityConversationProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const ahpDark = isAhp && isDark;

  const {
    messages,
    phase,
    intakePhase,
    procedures,
    scenarioName,
    messagesUsed,
    maxMessages,
    isStreaming,
    isBuilding,
    isFinishing,
    inputType,
    skipButton,
    sendMessage,
    handleQuickReply,
    handlePaymentComplete,
    handleSkip,
    finalize,
  } = useDignityChat();

  const [input, setInput] = useState('');
  const [showPreviewMobile, setShowPreviewMobile] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const showInput = phase === 'intake' && intakePhase !== 'summary';
  const showChatInput = phase === 'chat';
  const canSend = input.trim().length > 0 && !isStreaming;
  const canFinalize = procedures.length > 0 && !isStreaming && phase === 'chat';

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isStreaming]);

  const handleSend = () => {
    if (!canSend) return;
    const val = inputType === 'zip'
      ? input.replace(/\D/g, '').slice(0, 5)
      : input.trim();
    sendMessage(val);
    setInput('');
    inputRef.current?.focus();
  };

  const handleFinalize = async () => {
    const result = await finalize();
    if (result) onFinalized(result);
  };

  // Get current quick replies from latest assistant message
  const lastAssistantMsg = [...messages].reverse().find(m => m.role === 'assistant');
  const currentQuickReplies = lastAssistantMsg?.quickReplies;
  const showQuickReplies = phase === 'intake' && intakePhase !== 'summary' && currentQuickReplies && currentQuickReplies.length > 0;

  return (
    <div className="flex flex-col h-[65vh] max-h-[700px]">
      {/* Main content area */}
      <div className="flex-1 flex gap-4 overflow-hidden">
        {/* Chat column */}
        <div className="flex-[3] flex flex-col min-w-0">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto thin-scrollbar p-4 space-y-4">
            {messages.map((msg) => (
              <MessageBubble
                key={msg.id}
                message={msg}
                isAhp={isAhp}
                ahpDark={ahpDark}
                isStreaming={isStreaming && msg.id === messages[messages.length - 1]?.id && msg.role === 'assistant'}
                onPaymentComplete={handlePaymentComplete}
              />
            ))}

            {/* Streaming indicator */}
            {isStreaming && (
              <div className="flex gap-3">
                <DignityAvatar size="static" className="w-8 h-8" />
                <div className={`rounded-2xl rounded-tl-sm px-4 py-2.5 ${
                  ahpDark ? 'bg-[var(--civic-navy-deep)]' : isAhp ? 'bg-gray-100' : 'bg-white/5'
                }`}>
                  <div className="flex gap-1">
                    <div className={`w-2 h-2 rounded-full animate-bounce ${isAhp ? 'bg-amber-500' : 'bg-amber-400'}`} style={{ animationDelay: '0ms' }} />
                    <div className={`w-2 h-2 rounded-full animate-bounce ${isAhp ? 'bg-amber-500' : 'bg-amber-400'}`} style={{ animationDelay: '150ms' }} />
                    <div className={`w-2 h-2 rounded-full animate-bounce ${isAhp ? 'bg-amber-500' : 'bg-amber-400'}`} style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick replies */}
          {showQuickReplies && (
            <div className={`px-4 py-2 border-t ${
              ahpDark ? 'border-civic-gold/15' : isAhp ? 'border-gray-100' : 'border-white/5'
            }`}>
              <div className="flex flex-wrap gap-1.5">
                {currentQuickReplies!.map((qr) => (
                  <button
                    key={qr.value}
                    onClick={() => handleQuickReply(qr.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all hover:scale-[1.02] ${
                      ahpDark
                        ? 'border-civic-gold/25 text-civic-warm hover:bg-civic-gold/10'
                        : isAhp
                        ? 'border-civic-money/30 text-civic-money hover:bg-civic-money/10'
                        : 'border-amber-500/30 text-amber-400 hover:bg-amber-500/10'
                    }`}
                  >
                    {qr.label}
                  </button>
                ))}
                {skipButton && (
                  <button
                    onClick={handleSkip}
                    className="px-3 py-1.5 rounded-full text-xs text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
                  >
                    <SkipForward className="w-3 h-3" />
                    {skipButton}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Input bar — intake phase (text or zip) */}
          {showInput && (
            <div className={`p-3 border-t ${
              ahpDark ? 'border-civic-gold/15' : isAhp ? 'border-gray-200' : 'border-white/10'
            }`}>
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => {
                    if (inputType === 'zip') {
                      setInput(e.target.value.replace(/\D/g, '').slice(0, 5));
                    } else {
                      setInput(e.target.value);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={
                    inputType === 'zip'
                      ? 'Enter ZIP code (e.g. 10001)'
                      : intakePhase === 'greeting'
                        ? 'Describe your medical situation...'
                        : 'Type your answer...'
                  }
                  className={`text-sm ${inputType === 'zip' ? 'w-40 font-mono' : ''}`}
                  maxLength={inputType === 'zip' ? 5 : undefined}
                />
                <Button
                  onClick={handleSend}
                  disabled={!canSend}
                  size="sm"
                  className={`shrink-0 ${
                    isAhp
                      ? 'bg-civic-money hover:bg-[#234d3b] text-white'
                      : 'bg-amber-500 hover:bg-amber-600 text-black'
                  }`}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Input bar — chat phase */}
          {showChatInput && (
            <div className={`p-3 border-t ${
              ahpDark ? 'border-civic-gold/15' : isAhp ? 'border-gray-200' : 'border-white/10'
            }`}>
              <div className="flex gap-2">
                <Input
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  placeholder={messagesUsed >= maxMessages ? 'Message limit reached' : 'Ask about your medical costs...'}
                  disabled={isStreaming || messagesUsed >= maxMessages}
                  className="text-sm"
                />
                <Button
                  onClick={handleSend}
                  disabled={!canSend || messagesUsed >= maxMessages}
                  size="sm"
                  className={`shrink-0 ${
                    isAhp
                      ? 'bg-civic-money hover:bg-[#234d3b] text-white'
                      : 'bg-amber-500 hover:bg-amber-600 text-black'
                  }`}
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Desktop: scenario preview (only in chat phase) */}
        {phase === 'chat' && (
          <div className="flex-[2] hidden md:flex min-w-[250px]">
            <AiScenarioPreview
              procedures={procedures}
              scenarioName={scenarioName}
              isBuilding={isBuilding}
            />
          </div>
        )}
      </div>

      {/* Mobile: collapsible preview (chat phase only) */}
      {phase === 'chat' && procedures.length > 0 && (
        <div className="md:hidden">
          <button
            onClick={() => setShowPreviewMobile(!showPreviewMobile)}
            className={`w-full flex items-center justify-between px-4 py-2 border-t ${
              ahpDark ? 'border-civic-gold/15 bg-civic-card-dark'
                : isAhp ? 'border-gray-200 bg-gray-50' : 'border-white/10 bg-black/20'
            }`}
          >
            <span className="text-xs font-medium">
              Scenario Preview ({procedures.length} procedures, ${procedures.reduce((s, p) => s + p.estimatedCost * p.quantity, 0).toLocaleString()})
            </span>
            {showPreviewMobile ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
          </button>
          {showPreviewMobile && (
            <div className="h-[200px]">
              <AiScenarioPreview
                procedures={procedures}
                scenarioName={scenarioName}
                isBuilding={isBuilding}
              />
            </div>
          )}
        </div>
      )}

      {/* Session controls (chat phase only) */}
      {phase === 'chat' && (
        <AiSessionControls
          messagesUsed={messagesUsed}
          maxMessages={maxMessages}
          canFinalize={canFinalize}
          onFinalize={handleFinalize}
          isFinishing={isFinishing}
        />
      )}
    </div>
  );
}

// ── Message bubble component ──

function MessageBubble({
  message,
  isAhp,
  ahpDark,
  isStreaming,
  onPaymentComplete,
}: {
  message: DignityMessage;
  isAhp: boolean;
  ahpDark: boolean;
  isStreaming?: boolean;
  onPaymentComplete: (token: string) => void;
}) {
  if (message.role === 'system') {
    return (
      <div className="flex justify-center">
        <span className={`text-[10px] px-3 py-1 rounded-full ${
          ahpDark ? 'bg-[var(--civic-navy-deep)] text-civic-gold' : isAhp ? 'bg-green-100 text-green-700' : 'bg-green-500/10 text-green-400'
        }`}>
          {message.content}
        </span>
      </div>
    );
  }

  const isUser = message.role === 'user';

  // Payment gate message
  if (message.isPaymentGate) {
    return (
      <div className="space-y-3">
        {/* Dignity's summary message */}
        <div className="flex gap-3">
          <DignityAvatar size="static" className="w-8 h-8" />
          <div className={`max-w-[85%] rounded-2xl rounded-tl-sm px-4 py-2.5 ${
            ahpDark ? 'bg-[var(--civic-navy-deep)] text-civic-warm' : isAhp ? 'bg-gray-100 text-gray-900' : 'bg-white/5 text-foreground'
          }`}>
            <div className="text-sm whitespace-pre-wrap leading-relaxed">
              {message.content.split('\n').map((line, i) => {
                // Render **bold** markdown
                if (line.startsWith('**') && line.includes(':**')) {
                  const parts = line.match(/\*\*(.*?)\*\*(.*)/);
                  if (parts) {
                    return <div key={i}><strong>{parts[1]}</strong>{parts[2]}</div>;
                  }
                }
                return <div key={i}>{line || '\u00A0'}</div>;
              })}
            </div>
          </div>
        </div>

        {/* Inline payment card */}
        <div className="pl-11">
          <InlineChatPaymentGate onPaymentComplete={onPaymentComplete} />
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 ${isUser ? 'flex-row-reverse' : ''}`}>
      {/* Avatar */}
      {isUser ? (
        <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs font-bold ${
          ahpDark ? 'bg-civic-gold text-civic-bg-dark' : isAhp ? 'bg-civic-money text-white' : 'bg-matrix-green text-black'
        }`}>
          You
        </div>
      ) : (
        <DignityAvatar size="static" className="w-8 h-8" />
      )}

      {/* Bubble */}
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 ${
        isUser
          ? ahpDark
            ? 'bg-civic-gold/20 text-civic-warm rounded-tr-sm'
            : isAhp
            ? 'bg-civic-money text-white rounded-tr-sm'
            : 'bg-matrix-green/20 text-foreground rounded-tr-sm'
          : ahpDark
            ? 'bg-[var(--civic-navy-deep)] text-civic-warm rounded-tl-sm'
            : isAhp
            ? 'bg-gray-100 text-gray-900 rounded-tl-sm'
            : 'bg-white/5 text-foreground rounded-tl-sm'
      }`}>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">
          {message.content}
          {isStreaming && (
            <span className={`inline-block w-1.5 h-4 ml-0.5 animate-pulse ${
              isAhp ? 'bg-amber-500' : 'bg-amber-400'
            }`} />
          )}
        </div>
      </div>
    </div>
  );
}
